import { formatDate } from '../format/dates';
import type { Entitlements } from './types';

/*
 * What the shop's own screen says about its subscription (docs/Platform_Billing_Design.md §8.2), as
 * one pure rule both clients can word from. The access state is the server's — this only decides
 * which sentence to show, to whom, and how loudly.
 *
 * Everyone sees the states that change what they can do: a clerk whose save is refused must be told
 * why. Only an administrator sees the renewal coming, because a counter clerk does not need to be
 * told about the shop's software bill — and a line nobody can act on is a line people learn to stop
 * reading.
 */

/** How far ahead an administrator is told the plan is about to run out. */
export const renewalNoticeDays = 15;

export interface SubscriptionNotice {
  /** One or two words, for widths the sentence does not fit — a phone must still be told. */
  short: string;
  message: string;
  /** `neutral` when nothing is wrong yet; amber while it can still write; red once it cannot. */
  tone: 'neutral' | 'warning' | 'negative';
}

const indiaOffsetMs = 330 * 60_000;
const dayMs = 86_400_000;

/**
 * The Indian calendar day an instant falls on. The paid-through instants the server sends are
 * Indian midnights, so this is exact for them, and the shops are Indian.
 */
export const indiaDay = (instant: string): string =>
  new Date(Date.parse(instant) + indiaOffsetMs).toISOString().slice(0, 10);

const dayBefore = (day: string): string =>
  new Date(Date.parse(`${day}T00:00:00Z`) - dayMs).toISOString().slice(0, 10);

export function subscriptionNotice(
  entitlements: Pick<Entitlements, 'state' | 'canWrite' | 'effectiveUntil'>,
  isAdministrator: boolean,
  now: Date = new Date(),
): SubscriptionNotice | null {
  const { state, canWrite, effectiveUntil: until } = entitlements;

  switch (state) {
    case 'Active': {
      if (!isAdministrator || !until) return null;
      if (Date.parse(until) - now.getTime() > renewalNoticeDays * dayMs) return null;

      // `until` is the midnight access ends, so the last day paid for is the day before it.
      return {
        short: 'Renewal due',
        message: `Plan paid until ${formatDate(dayBefore(indiaDay(until)))} — renew to keep making changes`,
        tone: 'neutral',
      };
    }

    case 'Trialing':
      return until
        ? {
            short: 'Trial',
            message: `Trial ends ${formatDate(dayBefore(indiaDay(until)))}`,
            tone: 'warning',
          }
        : null;

    // `until` here is when grace runs out — the day it goes read-only, not the day payment fell
    // due. The sentence used to say "Payment due" over that date, which read as more time than
    // there was.
    case 'InGrace':
      return {
        short: 'Payment overdue',
        message: until
          ? `Payment overdue — read-only from ${formatDate(indiaDay(until))}`
          : 'Payment overdue',
        tone: 'warning',
      };

    case 'ReadOnly':
      return {
        short: 'Read-only',
        message: 'Read-only — settle your invoice to make changes',
        tone: canWrite ? 'warning' : 'negative',
      };

    default:
      return { short: 'Inactive', message: 'Subscription inactive', tone: 'negative' };
  }
}
